import * as React from "react";
import { ComponentProps } from "react";
import { cn } from "@repo/utils/cn";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { Button, ButtonProps } from "./Button";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ListProps, List } from "./List";
import {
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { sectionWrapper } from "./sectionWrappers";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";

export type StepData = {
  numberIcon?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  title?: string;
  image?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
};

export type StepDisplayProps = Omit<ComponentProps<"section">, "children"> & {
  typography?: Array<
    | { type: "title"; props: TitleProps }
    | { type: "text"; props: SimpleRichTextProps }
    | { type: "list"; props: ListProps }
  >;
  buttons?: ButtonProps[];
  steps: StepData[];
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  className?: string;
  onStepInteraction?: (stepIndex: number, stepTitle: string) => void;
};

export const StepDisplay = React.forwardRef<HTMLElement, StepDisplayProps>(
  (
    {
      typography,
      buttons,
      steps,
      className,
      backgroundColor = "none",
      topPadding = "none",
      bottomPadding = "none",
      onStepInteraction,
      ...props
    },
    ref,
  ) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ align: "center" });
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [activeDesktopStep, setActiveDesktopStep] = React.useState(0);

    const onSelect = React.useCallback(() => {
      if (!emblaApi) return;
      const newIndex = emblaApi.selectedScrollSnap();
      setSelectedIndex(newIndex);

      if (onStepInteraction && steps[newIndex]?.title) {
        onStepInteraction(newIndex, steps[newIndex].title || "");
      }
    }, [emblaApi, onStepInteraction, steps]);

    React.useEffect(() => {
      if (!emblaApi) return;
      emblaApi.on("select", onSelect);
    }, [emblaApi, onSelect]);

    const scrollTo = React.useCallback(
      (index: number) => emblaApi && emblaApi.scrollTo(index),
      [emblaApi],
    );

    return (
      <section
        ref={ref}
        className={sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
        })}
        {...props}
      >
        <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:px-[var(--container-margin)]">
          <div
            className={cn(
              "uipm:flex uipm:flex-col uipm:md:flex-row uipm:max-w-[1440px]",
              className,
            )}
            {...props}
          >
            {/* Typography and Buttons Section */}
            <div className="uipm:flex-1 uipm:mb-[var(--padding-padding-default)] uipm:md:mb-0 uipm:md:w-1/2 uipm:md:pr-[var(--margin-margin-default)]">
              <div className="uipm:flex uipm:flex-col uipm:gap-[var(--margin-margin-sm)] uipm:md:gap-[var(--margin-margin-default)]">
                {typography?.map((item, index) => {
                  switch (item.type) {
                    case "title":
                      return (
                        <Title
                          key={index}
                          {...item.props}
                          className={cn(
                            "uipm:text-[var(--typography-colors-text-heading)]",
                            item.props.className,
                          )}
                        />
                      );
                    case "text":
                      return (
                        <SimpleRichText
                          key={index}
                          {...item.props}
                          className={cn(
                            "uipm:text-[var(--typography-colors-text-faded-dark)]",
                            item.props.className,
                          )}
                        />
                      );
                    case "list":
                      return <List key={index} {...item.props} />;
                    default:
                      return null;
                  }
                })}

                {buttons && (
                  <div className="uipm:flex uipm:flex-col uipm:md:flex-row uipm:gap-[var(--margin-margin-sm)] uipm:pt-[var(--margin-margin-md)] uipm:md:pt-[24px]">
                    {buttons.map((buttonProps, index) => (
                      <Button key={index} {...buttonProps} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Steps Section */}
            <div className="uipm:flex-1 uipm:md:w-1/2">
              {/* Mobile: Carousel with indicators */}
              <div className="uipm:md:hidden">
                <div className="uipm:overflow-hidden" ref={emblaRef}>
                  <div className="uipm:flex">
                    {steps.map((step, index) => (
                      <div
                        key={index}
                        className="uipm:flex-[0_0_100%] uipm:min-w-0 uipm:px-[var(--margin-margin-default)]"
                      >
                        <div className="uipm:flex uipm:flex-col uipm:gap-[var(--padding-padding-default)] uipm:items-center">
                          {/* Image Section */}
                          {step.image && (
                            <div className="uipm:flex uipm:justify-center">
                              <ImageWithAspectRatio
                                {...step.image}
                                width={176}
                                height={216}
                                horizontalAlignmentMobile="center"
                                priority={index === 0}
                              />
                            </div>
                          )}

                          {/* Number Icon and Title Section */}
                          <div className="uipm:text-center">
                            <div className="uipm:flex uipm:justify-center uipm:mb-4">
                              {step.numberIcon ? (
                                <Image
                                  src={step.numberIcon.src}
                                  alt={step.numberIcon.alt}
                                  width={step.numberIcon.width || 18}
                                  height={step.numberIcon.height || 26}
                                  className="uipm:h-8 uipm:w-auto"
                                />
                              ) : (
                                <div className="uipm:w-[18px] uipm:h-8 uipm:rounded-lg uipm:bg-[var(--surface-surface-accent)] uipm:text-[var(--typography-colors-text-inverted)] uipm:flex uipm:items-center uipm:justify-center uipm:text-xl uipm:font-semibold">
                                  {index + 1}
                                </div>
                              )}
                            </div>
                            {step.title && (
                              <div
                                className={cn(
                                  "uipm:text-lg uipm:text-[var(--typography-colors-text-heading)]",
                                  index === selectedIndex
                                    ? "uipm:font-semibold"
                                    : "uipm:font-normal",
                                )}
                              >
                                {step.title}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Indicators */}
                <div
                  className="uipm:flex uipm:justify-center uipm:gap-[var(--container-gutter)] uipm:mt-6"
                  style={{ marginTop: "24px" }}
                >
                  {steps.map((step, index) => (
                    <button
                      key={index}
                      onClick={() => scrollTo(index)}
                      aria-label={`Go to step ${index + 1}${step.title ? `: ${step.title}` : ""}`}
                      className={cn(
                        "uipm:transition-all uipm:duration-200 uipm:rounded-lg uipm:min-w-[44px] uipm:min-h-[44px] uipm:flex uipm:items-center uipm:justify-center",
                      )}
                    >
                      <span
                        className={cn(
                          "uipm:rounded-lg uipm:transition-all uipm:duration-200",
                          index === selectedIndex
                            ? "uipm:w-5 uipm:h-2 uipm:bg-[var(--surface-surface-accent)]"
                            : "uipm:w-2 uipm:h-2 uipm:bg-[var(--surface-surface-secondary)]",
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Desktop: Cards in row */}
              <div className="uipm:hidden uipm:md:flex uipm:md:gap-[var(--margin-margin-sm)] uipm:items-center uipm:min-h-[380px]">
                {steps.map((step, index) => {
                  const isActive = index === activeDesktopStep;

                  return (
                    <div
                      key={index}
                      className={cn(
                        "uipm:flex-1 uipm:rounded-[var(--radius-radius-lg)] uipm:cursor-pointer uipm:step-card",
                        isActive
                          ? "uipm:p-[var(--padding-padding-default)] uipm:bg-[var(--surface-surface-accent-secondary)] uipm:step-card-active uipm:h-[380px]"
                          : "uipm:py-[var(--padding-padding-default)] uipm:px-[var(--padding-padding-sm)] uipm:bg-[var(--surface-surface-secondary)] uipm:h-[356px]",
                      )}
                      onMouseEnter={() => {
                        setActiveDesktopStep(index);
                        if (onStepInteraction && step.title) {
                          onStepInteraction(index, step.title);
                        }
                      }}
                    >
                      <div
                        className={cn(
                          "uipm:flex uipm:flex-col uipm:items-center uipm:text-center uipm:gap-[var(--padding-padding-default)]",
                          isActive && "uipm:w-[190px]",
                        )}
                      >
                        {/* Number Icon */}
                        <div className="uipm:flex uipm:justify-center">
                          {step.numberIcon ? (
                            <Image
                              src={step.numberIcon.src}
                              alt={step.numberIcon.alt}
                              width={step.numberIcon.width || 18}
                              height={step.numberIcon.height || 32}
                              className="uipm:h-8 uipm:w-auto"
                            />
                          ) : (
                            <div className="uipm:w-[18px] uipm:h-8 uipm:rounded-lg uipm:bg-[var(--surface-surface-accent)] uipm:text-[var(--typography-colors-text-inverted)] uipm:flex uipm:items-center uipm:justify-center uipm:text-sm uipm:font-semibold">
                              {index + 1}
                            </div>
                          )}
                        </div>

                        {/* Title */}
                        {step.title && (
                          <div
                            className={cn(
                              "uipm:text-lg uipm:text-[var(--typography-colors-text-heading)]",
                              isActive ? "uipm:font-bold" : "uipm:font-normal",
                            )}
                          >
                            {step.title}
                          </div>
                        )}

                        {/* Image - only show when active */}
                        {isActive && step.image && (
                          <div className="uipm:flex uipm:justify-center">
                            <ImageWithAspectRatio
                              {...step.image}
                              className="uipm:w-[176px] uipm:h-[216px] uipm:object-cover uipm:rounded-lg"
                              priority={index === 0}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  },
);

StepDisplay.displayName = "StepDisplay";
